import { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronDown, Clock, Facebook, Globe, House, Instagram, MapPin, Monitor, Navigation, Phone, Sparkles, Star, Store } from "lucide-react";
import "./staffPage.css";
import generalRequest from "../../request/generalRequest";
import StaffPageSkeleton from "./staffSkeleton/staffPageSkeleton";
import BookingModal from "../bookingModal/bookingModal";
import { CombinedContext } from "../../context/combinedContext";
import translations from "../../translations/translations";
import ShopMapPreview from "../../utils/shopMap/shopMapPreview";
import ShopGallery from "../common/shopGallery/shopGallery";

/* Height of the sticky category bar — keep in sync with --sfp-sticky in staffPage.css */
const STICKY_OFFSET = 78;

const CURRENCY_SYMBOLS = { USD: "$", DOP: "RD$", EUR: "€", CAD: "C$", MXN: "MX$" };

const formatPrice = (price, currency) => {
	const code = currency || "USD";
	const amount = Number(price ?? 0).toLocaleString("en-US");
	return CURRENCY_SYMBOLS[code] ? `${CURRENCY_SYMBOLS[code]}${amount}` : `${code} ${amount}`;
};

const formatTime24To12 = (time24) => {
	if (!time24) return "";
	const [h, m] = time24.split(":").map(Number);
	const period = h >= 12 ? "PM" : "AM";
	const hour12 = h % 12 || 12;
	return `${hour12}:${m.toString().padStart(2, "0")} ${period}`;
};

const toMinutes = (time24) => {
	if (!time24) return null;
	const [h, m] = time24.split(":").map(Number);
	if (Number.isNaN(h)) return null;
	return h * 60 + (m || 0);
};

/* Live open/closed badge computed from the shop's opening hours. */
const getOpenStatus = (openingHours, copy, language) => {
	if (!Array.isArray(openingHours) || openingHours.length < 7) return null;

	const now = new Date();
	const todayIndex = (now.getDay() + 6) % 7;
	const today = openingHours[todayIndex];
	if (!today) return null;

	const closedWith = (detail) => ({ isOpen: false, word: copy.closed, detail });

	const nextOpening = () => {
		for (let step = 1; step <= 7; step += 1) {
			const day = openingHours[(todayIndex + step) % 7];
			if (!day || day.closed || !day.open) continue;
			const when = step === 1 ? copy.tomorrow : language === "es" ? day.dayES : day.dayEN;
			return `${copy.opensAt} ${when} ${formatTime24To12(day.open)}`;
		}
		return null;
	};

	if (today.closed) return closedWith(nextOpening());

	const open = toMinutes(today.open);
	const close = toMinutes(today.close);
	if (open === null || close === null) return null;

	const minutes = now.getHours() * 60 + now.getMinutes();
	const spansMidnight = close <= open;
	const isOpen = spansMidnight ? minutes >= open || minutes < close : minutes >= open && minutes < close;

	if (isOpen) return { isOpen: true, word: copy.open, detail: `${copy.until} ${formatTime24To12(today.close)}` };
	if (!spansMidnight && minutes < open) return closedWith(`${copy.opensAt} ${formatTime24To12(today.open)}`);
	return closedWith(nextOpening());
};

const formatPhone = (number) => {
	if (!number) return "";
	const digits = number.replace(/\D/g, "");
	if (digits.length === 11 && digits.startsWith("1")) {
		return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
	}
	return number.startsWith("+") ? number : `+${number}`;
};

const formatUrl = (url) => {
	if (!url) return "";
	return url.startsWith("http://") || url.startsWith("https://") ? url : `https://${url}`;
};

const TikTokIcon = ({ size = 18 }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
		<path d="M16.6 3h-3.2v13.1a2.9 2.9 0 1 1-2.2-2.8v-3.3a6.2 6.2 0 1 0 5.4 6.1V9.4a8.6 8.6 0 0 0 4.4 1.4V7.6A5.5 5.5 0 0 1 16.6 3Z" />
	</svg>
);

const Stars = ({ value, size = 14 }) => {
	const rounded = Math.round(Number(value) || 0);
	return (
		<span className="sf-shop-stars" aria-label={`${value} / 5`}>
			{[1, 2, 3, 4, 5].map((i) => (
				<Star key={i} size={size} strokeWidth={1.5} fill={i <= rounded ? "currentColor" : "none"} className={i <= rounded ? "is-on" : "is-off"} />
			))}
		</span>
	);
};

const StaffPage = () => {
	const { shopId, staffId } = useParams();
	const { selectedService, setSelectedService, language } = useContext(CombinedContext);

	const [staff, setStaff] = useState(null);
	const [shopData, setShopData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [isBookingOpen, setIsBookingOpen] = useState(false);
	const [activeCategory, setActiveCategory] = useState(null);
	const [openCategories, setOpenCategories] = useState({});
	const [showFullWeek, setShowFullWeek] = useState(false);

	const categoryRefs = useRef({});
	const lockScrollSpy = useRef(0);
	const navigate = useNavigate();

	const t = translations[language].shopPage;
	const ts = translations[language].staffPage;

	useEffect(() => {
		if (!staffId) return;
		const load = async () => {
			try {
				const res = await generalRequest.getStaffInfoPublic(staffId);
				const s = res.staff;
				setStaff({
					staffId: s.staffId,
					firstName: s.firstName,
					lastName: s.lastName,
					role: s.role || "",
					image: s.photo || null,
					bio: s.bio || "",
					phone: s.phone || "",
					services: s.services || [],
					reviews: Array.isArray(s.reviews) ? s.reviews : [],
				});
				setShopData({ ...s.shop, lat: s.shop.lat, lng: s.shop.lng });
				setLoading(false);
			} catch (err) {
				console.error("Failed to fetch staff:", err);
				setError(t.errorLoading);
				setLoading(false);
			}
		};
		load();
	}, [staffId]);

	// Same for staff links: keep the shop segment pointing at the current slug.
	useEffect(() => {
		if (!shopData?.slug || shopId.startsWith("shop_")) return;
		if (shopId === shopData.slug) return;

		navigate(`/shops/${shopData.slug}/staff/${staffId}`, { replace: true });
	}, [shopData, shopId, staffId, navigate]);

	const servicesByCategory =
		!loading && staff && shopData
			? (shopData.serviceCategories || [])
					.map((cat) => ({ category: cat, services: staff.services.filter((s) => s.categoryId === cat.categoryId) }))
					.filter(({ services }) => services.length > 0)
			: [];

	useEffect(() => {
		if (!servicesByCategory.length) return;
		setOpenCategories(Object.fromEntries(servicesByCategory.map(({ category }) => [category.categoryId, true])));
		setActiveCategory(servicesByCategory[0].category.categoryId);
	}, [shopData, staff]);

	/* Keep the sticky tab bar in sync with whatever category is under it. */
	useEffect(() => {
		if (!servicesByCategory.length) return undefined;
		const ids = servicesByCategory.map(({ category }) => category.categoryId);

		const onScroll = () => {
			if (Date.now() < lockScrollSpy.current) return;
			let current = ids[0];
			ids.forEach((id) => {
				const el = categoryRefs.current[id];
				if (el && el.getBoundingClientRect().top <= STICKY_OFFSET + 12) current = id;
			});
			setActiveCategory((prev) => (prev === current ? prev : current));
		};

		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, [shopData, staff]);

	if (loading) return <StaffPageSkeleton />;
	/* a failed load used to sit on the skeleton forever */
	if (error || !staff || !shopData) return <p className="sf-shop-error">{error || t.errorLoading}</p>;

	const goToShop = () => navigate(`/shops/${shopId}`, { state: { fromStaff: true } });

	const jumpToCategory = (id) => {
		setActiveCategory(id);
		lockScrollSpy.current = Date.now() + 700;
		categoryRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
	};

	const toggleCategory = (id) => setOpenCategories((prev) => ({ ...prev, [id]: prev[id] === false }));

	const openBookingForService = (serviceId) => {
		setSelectedService(serviceId);
		setIsBookingOpen(true);
	};

	const fullName = [staff.firstName, staff.lastName].filter(Boolean).join(" ");
	const addressQuery = encodeURIComponent(`${shopData.address}, ${shopData.city}, ${shopData.country}`);
	const reviews = staff.reviews;
	const reviewCount = reviews.length;
	const rating = (reviewCount > 0 ? reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviewCount : 0).toFixed(1);

	const roleLabel = ts.sidebar?.staff?.roles?.[staff.role] || null;
	const status = getOpenStatus(shopData.openingHours, t.status, language);
	const serviceCount = staff.services.length;
	const showCategoryChrome = servicesByCategory.length > 1;
	const todayIndex = (new Date().getDay() + 6) % 7;
	const todayHours = Array.isArray(shopData.openingHours) ? shopData.openingHours[todayIndex] : null;
	const teammates = (shopData.staffs || []).filter((b) => b.staffId !== staff.staffId);

	const socials = [
		{ key: "instagram", url: shopData.socialLinks?.instagram, Icon: Instagram, label: "Instagram" },
		{ key: "facebook", url: shopData.socialLinks?.facebook, Icon: Facebook, label: "Facebook" },
		{ key: "tiktok", url: shopData.socialLinks?.tiktok, Icon: TikTokIcon, label: "TikTok" },
		{ key: "website", url: shopData.socialLinks?.website, Icon: Globe, label: t.sidebar.websiteTitle || "Website" },
	].filter((s) => s.url);

	const serviceTag = (service) => {
		const loc = service.locationType || (service.isHomeService ? "home" : "in_shop");
		if (loc === "home") {
			return (
				<span className="sf-shop-service-tag is-home">
					<House size={12} strokeWidth={2} />
					{t.main.serviceType.home}
				</span>
			);
		}
		if (loc === "online") {
			return (
				<span className="sf-shop-service-tag is-online">
					<Monitor size={12} strokeWidth={2} />
					{t.main.serviceType.online}
				</span>
			);
		}
		return null;
	};

	return (
		<div className="sf-staff-page">
			<div className="sf-staff-content">
				<div className="sf-staff-main">
					<ShopGallery
						images={shopData.images || []}
						shareUrl={`https://reservasinfila.com/shops/${shopData.slug || shopData.shopId}/staff/${staff.staffId}`}
						onBack={goToShop}
					/>

					{/* ── IDENTITY ── */}
					<header className="sf-staff-head">
						<div className="sf-staff-avatar-wrap">
							{staff.image ? (
								<img className="sf-staff-avatar" src={staff.image} alt="" />
							) : (
								<span className="sf-staff-avatar is-fallback">{staff.firstName?.charAt(0).toUpperCase()}</span>
							)}
						</div>

						<div className="sf-staff-head-copy">
							{roleLabel && <span className="sf-shop-eyebrow">{roleLabel}</span>}
							<h1 className="sf-shop-name">{fullName}</h1>

							<div className="sf-shop-meta">
								{reviewCount > 0 ? (
									<span className="sf-shop-rating-pill">
										<Star size={14} strokeWidth={1.5} fill="currentColor" />
										<strong>{rating}</strong>
										<span>({t.rating.reviews.replace("{{count}}", reviewCount)})</span>
									</span>
								) : (
									<span className="sf-shop-new-pill">
										<Sparkles size={13} strokeWidth={2} />
										{t.main.newShop}
									</span>
								)}

								{status && (
									<span className={`sf-shop-status ${status.isOpen ? "is-open" : "is-closed"}`}>
										<i className="sf-shop-status-dot" />
										<strong>{status.word}</strong>
										{status.detail && <span>· {status.detail}</span>}
									</span>
								)}
							</div>

							{/* the shop is context here, not the subject — a quiet link back */}
							<button type="button" className="sf-staff-shop-link" onClick={goToShop}>
								<Store size={15} strokeWidth={1.7} />
								<span>
									<em>{ts.at}</em>
									<strong>{shopData.name}</strong>
								</span>
							</button>

							<p className="sf-shop-address">
								<MapPin size={15} strokeWidth={1.7} />
								<span>
									{shopData.address}
									{shopData.addressDescription && <em>{shopData.addressDescription}</em>}
								</span>
							</p>
						</div>
					</header>

					{staff.bio && (
						<section className="sf-shop-about">
							<p>{staff.bio}</p>
						</section>
					)}

					{/* ── SERVICES ── */}
					<section className="sf-shop-section">
						<div className="sf-shop-section-head">
							<h2 className="sf-shop-h2">{t.main.services}</h2>
							{serviceCount > 0 && <span className="sf-shop-count">{serviceCount}</span>}
						</div>

						{serviceCount > 0 ? (
							<div className="sf-shop-services">
								{showCategoryChrome && (
									<div className="sf-shop-cat-tabs">
										{servicesByCategory.map(({ category, services }) => (
											<button
												key={category.categoryId}
												type="button"
												className={`sf-shop-cat-tab ${activeCategory === category.categoryId ? "is-active" : ""}`}
												onClick={() => jumpToCategory(category.categoryId)}
											>
												{category.name?.[language]}
												<span>{services.length}</span>
											</button>
										))}
									</div>
								)}

								{servicesByCategory.map(({ category, services }) => {
									const isOpen = !showCategoryChrome || openCategories[category.categoryId] !== false;
									return (
										<div key={category.categoryId} ref={(el) => (categoryRefs.current[category.categoryId] = el)} className="sf-shop-cat-card">
											{showCategoryChrome && (
												<button
													type="button"
													className="sf-shop-cat-header"
													onClick={() => toggleCategory(category.categoryId)}
													aria-expanded={isOpen}
												>
													<h3>{category.name?.[language]}</h3>
													<span className="sf-shop-cat-meta">
														{services.length}
														<ChevronDown size={16} className={`sf-shop-chevron ${isOpen ? "is-open" : ""}`} />
													</span>
												</button>
											)}

											<div className={`sf-shop-cat-body ${isOpen ? "is-open" : ""}`}>
												<div>
													{services.map((s) => (
														<div key={s.serviceId} className="sf-shop-service">
															<div className="sf-shop-service-copy">
																<h4 className="sf-shop-service-name">
																	{s.name}
																	{serviceTag(s)}
																</h4>
																{s.description && <p className="sf-shop-service-desc">{s.description}</p>}
																<span className="sf-shop-service-time">
																	<Clock size={13} strokeWidth={1.8} />
																	{s.duration} min
																</span>
															</div>

															<div className="sf-shop-service-action">
																<span className="sf-shop-service-price">{formatPrice(s.price, s.currency)}</span>
																<button type="button" className="sf-shop-book-btn" onClick={() => openBookingForService(s.serviceId)}>
																	{ts.book}
																</button>
															</div>
														</div>
													))}
												</div>
											</div>
										</div>
									);
								})}
							</div>
						) : (
							<div className="sf-shop-empty">{ts.services.empty}</div>
						)}
					</section>

					{/* ── REVIEWS (this staff member's own) ── */}
					{reviewCount > 0 && (
						<section className="sf-shop-section">
							<div className="sf-shop-section-head">
								<h2 className="sf-shop-h2">{t.main.reviews}</h2>
								<span className="sf-shop-count">{reviewCount}</span>
							</div>

							<div className="sf-shop-rev-summary">
								<div className="sf-shop-rev-score">
									<span className="sf-shop-rev-score-num">{rating}</span>
									<Stars value={rating} size={16} />
									<span className="sf-shop-rev-score-count">{t.rating.reviews.replace("{{count}}", reviewCount)}</span>
								</div>

								<div className="sf-shop-rev-bars">
									{[5, 4, 3, 2, 1].map((star) => {
										const count = reviews.filter((r) => r.rating === star).length;
										const percent = reviewCount > 0 ? (count / reviewCount) * 100 : 0;
										return (
											<div key={star} className="sf-shop-rev-bar-row">
												<span className="sf-shop-rev-bar-label">
													{star}
													<Star size={11} strokeWidth={0} fill="currentColor" />
												</span>
												<span className="sf-shop-rev-bar">
													<span className="sf-shop-rev-bar-fill" style={{ width: `${percent}%` }} />
												</span>
												<span className="sf-shop-rev-bar-count">{count}</span>
											</div>
										);
									})}
								</div>
							</div>

							<div className="sf-shop-rev-list">
								{reviews.map((rev, i) => (
									<article key={rev.reviewId || i} className="sf-shop-rev-card">
										<div className="sf-shop-rev-head">
											<div className="sf-shop-rev-who">
												<span className="sf-shop-rev-avatar">{(rev.customerName || "?").charAt(0).toUpperCase()}</span>
												<span>
													<span className="sf-shop-rev-name">{rev.customerName || t.main.anonymous}</span>
													{rev.createdAt && (
														<span className="sf-shop-rev-date">
															{new Date(rev.createdAt).toLocaleDateString(language === "es" ? "es-DO" : "en-US", {
																year: "numeric",
																month: "short",
																day: "numeric",
															})}
														</span>
													)}
												</span>
											</div>
											<Stars value={rev.rating} size={13} />
										</div>

										{rev.services?.length > 0 && (
											<div className="sf-shop-rev-chips">
												{rev.services.map((srv, j) => (
													<span key={j} className="sf-shop-rev-chip">
														{language === "es" ? srv.serviceNameEs : srv.serviceNameEn}
														<em>{srv.duration} min</em>
													</span>
												))}
											</div>
										)}

										{rev.comment && <p className="sf-shop-rev-text">{rev.comment}</p>}
									</article>
								))}
							</div>
						</section>
					)}
				</div>

				{/* ── STICKY SIDEBAR ── */}
				<aside className="sf-staff-sidebar">
					<div className="sf-shop-card">
						{shopData.lat && shopData.lng && (
							<div className="sf-shop-map">
								<ShopMapPreview lat={shopData.lat} lng={shopData.lng} />
							</div>
						)}

						<div className="sf-shop-card-body">
							<div className="sf-shop-place">
								<MapPin size={16} strokeWidth={1.7} />
								<span>
									<strong>{shopData.address}</strong>
									{(shopData.city || shopData.country) && <em>{[shopData.city, shopData.country].filter(Boolean).join(", ")}</em>}
								</span>
							</div>

							<a
								className="sf-shop-directions"
								href={`https://www.google.com/maps/dir/?api=1&destination=${addressQuery}`}
								target="_blank"
								rel="noopener noreferrer"
							>
								<Navigation size={15} strokeWidth={2} />
								{t.sidebar.getDirections}
							</a>

							<div className="sf-shop-rule" />

							<p className="sf-shop-label">{t.sidebar.contactAndHours}</p>
							<div className="sf-shop-contact">
								<a href={staff.phone || shopData.phone ? `tel:${staff.phone || shopData.phone}` : undefined} className="sf-shop-phone">
									<Phone size={15} strokeWidth={1.7} />
									{staff.phone || shopData.phone ? formatPhone(staff.phone || shopData.phone) : t.sidebar.noPhone}
								</a>
								{(staff.phone || shopData.phone) && (
									<a href={`tel:${staff.phone || shopData.phone}`} className="sf-shop-call">
										{t.sidebar.call}
									</a>
								)}
							</div>

							{todayHours && (
								<div className="sf-shop-hours">
									<div className="sf-shop-hours-today">
										<span className="sf-shop-hours-day">
											<Clock size={14} strokeWidth={1.8} />
											{t.sidebar.hours.today}
										</span>
										<span className="sf-shop-hours-time">
											{todayHours.closed
												? t.sidebar.hours.closed
												: `${formatTime24To12(todayHours.open)} – ${formatTime24To12(todayHours.close)}`}
										</span>
									</div>

									<button type="button" className="sf-shop-hours-toggle" onClick={() => setShowFullWeek((v) => !v)}>
										{showFullWeek ? t.sidebar.hours.hideWeek : t.sidebar.hours.showWeek}
										<ChevronDown size={14} className={`sf-shop-chevron ${showFullWeek ? "is-open" : ""}`} />
									</button>

									<div className={`sf-shop-hours-week ${showFullWeek ? "is-open" : ""}`}>
										<div>
											{shopData.openingHours.map((dayObj, index) =>
												dayObj ? (
													<div key={index} className={`sf-shop-hours-row ${todayIndex === index ? "is-today" : ""}`}>
														<span>{language === "es" ? dayObj.dayES : dayObj.dayEN}</span>
														<span>
															{dayObj.closed
																? t.sidebar.hours.closed
																: `${formatTime24To12(dayObj.open)} – ${formatTime24To12(dayObj.close)}`}
														</span>
													</div>
												) : null,
											)}
										</div>
									</div>
								</div>
							)}

							{socials.length > 0 && (
								<>
									<div className="sf-shop-rule" />
									<p className="sf-shop-label">{t.sidebar.socialTitle}</p>
									<div className="sf-shop-socials">
										{socials.map(({ key, url, Icon, label }) => (
											<a key={key} href={formatUrl(url)} target="_blank" rel="noopener noreferrer" className="sf-shop-social" title={label}>
												<Icon size={18} strokeWidth={1.7} />
												<span>{label}</span>
											</a>
										))}
									</div>
								</>
							)}
						</div>
					</div>

					{teammates.length > 0 && (
						<div className="sf-staff-team-card">
							<p className="sf-shop-label">{ts.otherStaff}</p>
							<div className="sf-shop-team">
								{teammates.map((b) => (
									<button
										type="button"
										className="sf-shop-team-card"
										key={b.staffId}
										onClick={() => navigate(`/shops/${shopData.slug || shopData.shopId}/staff/${b.staffId}`)}
									>
										{b.photo ? (
											<img className="sf-shop-team-avatar" src={b.photo} alt={b.firstName || ""} loading="lazy" />
										) : (
											<span className="sf-shop-team-avatar is-fallback">{b.firstName?.charAt(0).toUpperCase()}</span>
										)}
										<span className="sf-shop-team-name">{b.firstName}</span>
									</button>
								))}
							</div>
						</div>
					)}
				</aside>
			</div>

			{isBookingOpen && (
				<BookingModal
					open={isBookingOpen}
					onClose={() => setIsBookingOpen(false)}
					availabilityId={shopData.shopId}
					shopData={shopData}
					initialServiceId={selectedService}
					initialstaffId={staff.staffId}
				/>
			)}
		</div>
	);
};

export default StaffPage;
